import axios, { AxiosRequestConfig } from "axios";
import {WEB_BASE_URL} from "../../../../config/config";

type ErrorHandler = (
    response: { statusCode: number; content?: any },
    error: Error
) => void;

type RefreshTokenHandler = () => Promise<string|undefined>;

export default class APIClient {
    private baseUri: string | undefined;
    private token: string | undefined;
    private readonly skipAuth: boolean = false;
    private errorHandler: ErrorHandler | undefined;
    private readonly refreshTokenHandler?: RefreshTokenHandler;
    private languageCode: string | undefined;

    constructor(baseUri?: string, skipAuth: boolean = false, token?: string, refreshTokenHandler?: RefreshTokenHandler, languageCode?: string) {
        this.baseUri = baseUri || WEB_BASE_URL || "";
        this.token = token;
        if (refreshTokenHandler)
        {
            this.refreshTokenHandler = refreshTokenHandler
        }
        this.skipAuth = skipAuth;
        this.languageCode = languageCode;
    }

    public setAuthToken(token: string): void {
        this.token = token;
    }

    public removeAuthToken(): void {
        this.token = undefined;
    }

    public onError(handler: ErrorHandler): void {
        this.errorHandler = handler;
    }

    public get<T = unknown>(endpoint: string, params?: unknown): Promise<T> {
        return this.makeRequest("get", endpoint, params);
    }

    public post<T = unknown>(endpoint: string, data?: unknown): Promise<T> {
        return this.makeRequest("post", endpoint, data);
    }

    public put<T = unknown>(endpoint: string, data?: unknown): Promise<T> {
        return this.makeRequest("put", endpoint, data);
    }

    public patch<T = unknown>(endpoint: string, data?: unknown): Promise<T> {
        return this.makeRequest("patch", endpoint, data);
    }

    public delete<T = unknown>(endpoint: string, data?: unknown): Promise<T> {
        return this.makeRequest("delete", endpoint, data);
    }

    public download(endpoint: string, params?: unknown): Promise<void> {
        return this.makeRequest("get", endpoint, params, true);
    }

    private async makeRequest<T = unknown>(
        method: "get" | "post" | "put" | "patch" | "delete",
        endpoint: string,
        data?: any,
        isDownload = false
    ): Promise<T> {
        const options: AxiosRequestConfig = {
            method,
            url: this.composeUrl(endpoint),
            responseType: isDownload ? "arraybuffer" : "json",
        };

        const headers: Record<string, string> = {
            "Content-Type": method === "patch" ? "application/merge-patch+json" : "application/json",
            "Accept": "application/json",
        };
        if (this.languageCode) {
            headers["Accept-Language"] = this.languageCode;
        }

        if (this.refreshTokenHandler) {
            this.token = await this.refreshTokenHandler();
        }
        // TODO: Review
        if (this.token && !this.skipAuth) {
            headers.authorization = `Bearer ${this.token}`;
        }

        options.headers = headers;

        if (data) {
            if (method === "get") {
                options.params = data;
            } else {
                if (Object.values(data).find(value => value instanceof File)) {
                    options.data = new FormData();
                    Object.keys(data).forEach(key => {
                        APIClient.fillFormData(options.data, key, data[key]);
                    });
                } else {
                    options.data = data;
                }
            }
        }

        return axios(options)
            .then(response => {
                if (!isDownload) {
                    return response.data;
                } else {
                    const contentType = response.headers["content-type"];
                    const disposition = response.headers["content-disposition"];
                    let filename = disposition
                        .split("filename=")[1]
                        .split(";")[0];
                    filename = filename
                        .replace(/["']/g, "")
                        .substring(0, filename.length);

                    const a = document.createElement("a");
                    const blob = new Blob([response.data], {
                        type: contentType,
                    });
                    const url = window.URL.createObjectURL(blob);

                    a.href = url;
                    a.download = filename;
                    document.body.append(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                }
            })
            .catch(e => {
                if (e.response) {
                    // The request was made and the server responded with a status code,
                    // then return the error message from the API.
                    const error = new Error(
                        e.response.data?.error ||
                            e.response.data?.message ||
                            e.response.data?.detail ||
                            "Unexpected error"
                    );

                    this.handleError(e, error);
                } else {
                    //No response from the API
                    this.handleError(e);
                }
            });
    }

    private handleError(e: any, handledError?: Error): void {
        if (this.errorHandler !== undefined) {
            this.errorHandler(
                {
                    statusCode: e.response?.status || 500,
                    content: e.response?.data,
                },
                handledError || e
            );
        } else {
            throw handledError || e;
        }
    }

    private composeUrl(endpoint: string): string {
        return this.baseUri + endpoint;
    }

    private static fillFormData(
        formData: FormData,
        name: string,
        values: any
    ): void {
        if (values instanceof File) {
            formData.append(name, values);
        } else if (typeof values === "object") {
            if (values === null) return;

            Object.keys(values).forEach(key => {
                const newKey = `${name}[${key}]`;
                if (typeof values[key] === "object") {
                    this.fillFormData(formData, newKey, values[key]);
                } else {
                    formData.append(
                        newKey,
                        this.composeFormDataValue(values[key])
                    );
                }
            });
        } else {
            formData.append(name, this.composeFormDataValue(values));
        }
    }

    private static composeFormDataValue(value: any) {
        if (typeof value === "boolean") {
            return value ? "1" : "0";
        }

        if (typeof value === "number") {
            return value + "";
        }

        return value;
    }
}
